/**
 * Утилиты форматирования данных для UI.
 */

import type { Language } from '@/utils/language';

/** Прочерк: значения нет (не ноль и не пустая строка) */
export const DASH = '\u2014';

/**
 * Форматирование epoch millis в читаемую дату.
 * Названия месяцев и порядок частей даёт локаль, а не жёсткий формат; день — в
 * часовом поясе timeZone (организации), без него — в поясе браузера.
 */
export function formatDate(
	epochMs: number | undefined | null,
	locale: Language,
	fallback = DASH,
	timeZone?: string,
): string {
	if (!epochMs || epochMs <= 0) return fallback;
	return new Date(epochMs).toLocaleDateString(locale, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		timeZone,
	});
}

/**
 * Название месяца момента («октябрь», «October») -- для имён по месяцу, как «Отчёт · октябрь»
 * макета. Месяц -- в часовом поясе timeZone, без него -- в поясе браузера.
 */
export function formatMonth(epochMs: number, locale: Language, timeZone?: string): string {
	return new Date(epochMs).toLocaleDateString(locale, { month: 'long', timeZone });
}

/**
 * День и месяц без года («17 мар.», «Mar 17») -- для подписей рядов по дням, где год един для
 * всего ряда. Сутки -- в часовом поясе timeZone, без него -- в поясе браузера.
 */
export function formatShortDate(
	epochMs: number | undefined | null,
	locale: Language,
	fallback = DASH,
	timeZone?: string,
): string {
	if (!epochMs || epochMs <= 0) return fallback;
	return new Date(epochMs).toLocaleDateString(locale, {
		month: 'short',
		day: 'numeric',
		timeZone,
	});
}

/**
 * Дата словами без сокращений -- «20 октября 2026», «October 20, 2026» (дата шапки макета,
 * index.html:89). Русская локаль Intl дописывает после года «г.»; буквенные вставки между
 * частями даты отбрасываются, разделители-знаки (запятая английской даты) остаются.
 */
export function formatLongDate(
	epochMs: number | undefined | null,
	locale: Language,
	fallback = DASH,
	timeZone?: string,
): string {
	if (!epochMs || epochMs <= 0) return fallback;
	return new Intl.DateTimeFormat(locale, {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		timeZone,
	})
		.formatToParts(epochMs)
		.filter((part) => part.type !== 'literal' || !/\p{L}/u.test(part.value))
		.map((part) => part.value)
		.join('')
		.trim();
}

/**
 * Форматирование epoch millis в полную дату со временем.
 */
export function formatDateTime(
	epochMs: number | undefined | null,
	locale: Language,
	fallback = DASH,
	timeZone?: string,
): string {
	if (!epochMs || epochMs <= 0) return fallback;
	return new Date(epochMs).toLocaleString(locale, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		timeZone,
	});
}

/**
 * Короткие дата и время «ДД.ММ, ЧЧ:ММ» без года — формат дат экранов интеграций макета
 * (integrationDate, pages-integrations.js:6-15); сейчас — «Последний успех» списка получателей
 */
export function formatShortDateTime(
	epochMs: number | undefined | null,
	locale: Language,
	fallback = DASH,
	timeZone?: string,
): string {
	if (!epochMs || epochMs <= 0) return fallback;
	return new Intl.DateTimeFormat(locale, {
		day: '2-digit',
		month: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		hourCycle: 'h23',
		timeZone,
	}).format(epochMs);
}

/** Время суток «ЧЧ:ММ» — для конца интервала, начало которого показано полной датой */
export function formatTime(
	epochMs: number | undefined | null,
	locale: Language,
	fallback = DASH,
	timeZone?: string,
): string {
	if (!epochMs || epochMs <= 0) return fallback;
	return new Date(epochMs).toLocaleTimeString(locale, {
		hour: '2-digit',
		minute: '2-digit',
		timeZone,
	});
}

/** Единицы размера по степеням 1024; обозначения латиницей на любом языке -- выбор за владельцем */
const SIZE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];
const SIZE_STEP = 1024;
/** Меньше десяти единиц -- с одной цифрой после запятой («1,5 MB»), дальше целым («15 MB») */
const SIZE_FRACTION_BELOW = 10;

/**
 * Форматирование размера в байтах в человекочитаемый вид: «1,5 MB» (ru),
 * «1.5 MB» (en). Десятичный разделитель -- по языку, единицы -- латиницей.
 * Общая реализация для всех разделов -- раньше несколько файлов определяли
 * её заново.
 */
export function formatSize(bytes: number, locale: Language): string {
	if (bytes === 0) return `0 ${SIZE_UNITS[0]}`;
	const i = Math.floor(Math.log(bytes) / Math.log(SIZE_STEP));
	const val = bytes / Math.pow(SIZE_STEP, i);
	const digits = val < SIZE_FRACTION_BELOW ? 1 : 0;
	const number = formatNumber(val, locale, {
		minimumFractionDigits: digits,
		maximumFractionDigits: digits,
	});
	return `${number} ${SIZE_UNITS[i]}`;
}

/**
 * Форматирование размера документного (NoSQL) хранилища -- база или
 * инстанс MongoDB/Cassandra. В отличие от formatSize выше, для этого типа
 * хранилища ноль не бывает настоящим измерением: агент qcap размер
 * документных баз не собирает и всегда присылает 0 (см. doc-comment
 * NosqlDatabase.size / NosqlInstance.size, api/nosqlDb.ts), а каскад
 * инстанса лишь суммирует эти нули дальше. Показ "0 B" выдавал бы
 * неизвестный размер за измеренный факт "на сервере ничего нет" --
 * возвращаем прочерк вместо этого.
 */
export function formatDocumentSize(bytes: number, locale: Language, fallback = DASH): string {
	return bytes === 0 ? fallback : formatSize(bytes, locale);
}

/**
 * Форматирование числа с разделителями разрядов по локали.
 * В русском разряды разделяет неразрывный пробел, в английском -- запятая.
 * Число знаков после запятой задаёт вызывающий (options), иначе -- правило локали.
 */
export function formatNumber(
	value: number,
	locale: Language,
	options?: Intl.NumberFormatOptions,
): string {
	return value.toLocaleString(locale, options);
}

/**
 * Компактная запись числа по локали: 15 300 -> «15,3 тыс.» (ru), «15.3K»
 * (en); меньше тысячи -- как есть, с одной цифрой после запятой. И
 * разделитель, и сокращение разряда -- по языку.
 */
export function formatCompactNumber(value: number, locale: Language): string {
	return new Intl.NumberFormat(locale, {
		notation: 'compact',
		maximumFractionDigits: 1,
	}).format(value);
}
